<script setup>
import combinationStorage from '@/services/combination-storage';
import CombinationProductFlow from '@/components/front/combination/CombinationProductFlow.vue';
import CombinationProductPort from '@/components/front/combination/CombinationProductPort.vue';
import CombinationProductCables from '@/components/front/combination/CombinationProductCables.vue';
import CombinationProductConnect from '@/components/front/combination/CombinationProductConnect.vue';
import { computed, defineOptions, ref, shallowRef, watch } from 'vue';
import swal from 'sweetalert';

defineOptions({
  name: 'CombinationProductApp'
});

window.addEventListener('beforeunload', (e) => {
  if (combinationStorage.beforeUnloadEnabled) {
    e.preventDefault();
    e.stopPropagation();
  }
});

const flowComponent = shallowRef({
  0: CombinationProductPort,
  1: CombinationProductCables,
  2: CombinationProductConnect,
  3: CombinationProductConnect
});

const chooseButtonText = ref({
  0: '選擇線材規格',
  1: '選擇串接方式',
  2: '確認訂單',
});

const toPrevFlow = ref({
  1: '重選兩端產品',
  2: '重選線材規格',
  3: '重選串接方式'
});

const showTip = ref(null);
const numberInput = ref(null);
const minNum = 1;
const maxNum = 99;

watch(() => combinationStorage.initialItem, () => {
  setMappingIds();
});

watch(() => combinationStorage.terminalItem, () => {
  setMappingIds();
});

function setMappingIds() {
  if (!combinationStorage.initialItem && !combinationStorage.terminalItem) {
    combinationStorage.mappingIds = null;
  }

  if (combinationStorage.initialItem && !combinationStorage.terminalItem) {
    combinationStorage.mappingIds = combinationStorage.initialItem.mappingIds;
  }

  if (!combinationStorage.initialItem && combinationStorage.terminalItem) {
    combinationStorage.mappingIds = combinationStorage.terminalItem.mappingIds;
  }

  if (combinationStorage.initialItem && combinationStorage.terminalItem) {
    combinationStorage.mappingIds = null;
  }
}

const total = computed(() => {
  let total = 0;

  total = total + parseFloat(combinationStorage.initialItem?.priceSet.final.price ?? 0);

  total = total + parseFloat(combinationStorage.terminalItem?.priceSet.final.price ?? 0);

  Object.values(combinationStorage.cables).forEach((v) => {
    if (v?.variant) {
      total = total + (parseFloat(v.variant.priceSet.final.price) * parseFloat(v.variant.length));
    }

    if (v?.initial.category) {
      total = total + parseFloat(v.initial.category.options.price);
    }

    if (v?.terminal.category) {
      total = total + parseFloat(v.terminal.category.options.price);
    }
  });

  total = total * combinationStorage.quantity;

  combinationStorage.total = total;

  return total;
});

function handleMinusButton() {
  if (combinationStorage.quantity > minNum) {
    combinationStorage.quantity--;
  }
}

function handlePlusButton() {
  if (combinationStorage.quantity < maxNum) {
    combinationStorage.quantity++;
  }
}

const onlyCables = computed(() => {
  return  (combinationStorage.initialItem === null && combinationStorage.terminalItem === null);
});

watch(() => onlyCables.value, (v) => {
  combinationStorage.onlyCables = v;
})

function toNextStep() {
  switch (combinationStorage.flowIndex) {
    case 0:
      if (combinationStorage.terminalItem && !combinationStorage.initialItem) {
        combinationStorage.initialItem = combinationStorage.terminalItem;
        combinationStorage.terminalItem = null;
      }

      combinationStorage.flowIndex++;

      goToTop();

      break;

    case 1:
      const cableLength = Object.values(combinationStorage.cables).length;
      let allowNullCount = 0;

      for (let i = 1; i <= cableLength; i++) {
        const allowNull = combinationStorage.cables[i].allowNull;
        allowNullCount = allowNull ? allowNullCount + 1 : allowNullCount;

        if (!combinationStorage.cables[i].variant && !allowNull) {
          showTipText('尚未選擇 線材規格');

          return false;
        }
      }

      if (allowNullCount === cableLength) {
        showTipText('至少有一項需要選擇線材規格');
      } else {
        combinationStorage.flowIndex++;

        goToTop();
      }

      break;
    case 2:
      for (let i = 1; i <= Object.values(combinationStorage.cables).length; i++) {
        combinationStorage.cables[i].isValid = true;

        const initial = combinationStorage.cables[i].initial;
        const terminal = combinationStorage.cables[i].terminal;
        if (!combinationStorage.onlyCables
          && !combinationStorage.cables[i].allowNull
          && (initial.pin && !initial.category)
          || (terminal.pin && !terminal.category)
        ) {
          combinationStorage.cables[i].isValid = false;

          showTipText('尚未選擇 串接方式 / 加工方式');

          document.getElementById('product-cable-connect-' + i)
            .scrollIntoView({ behavior: 'smooth', block: 'center' });

          return false;
        }
      }
      combinationStorage.flowIndex++;

      goToTop();

      break;
  }
}

function showTipText(text) {
  showTip.value = text;

  setTimeout(() => {
    showTip.value = null;
  }, 5000);
}

function toPrevStep() {
  if (combinationStorage.flowIndex !== 0) {
    combinationStorage.flowIndex--;

    goToTop();
  }
}

function goToTop() {
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
}

async function addToCart(goToCart) {
  const item = prepareItem();

  combinationStorage.beforeUnloadEnabled = false;

  try {
    const res = await u.$http.post(
      '@cart_ajax/addCombinationItemToCart',
      {
        item: item,
        quantity: combinationStorage.quantity,
      }
    );

    updateCartButton(res.data.data);

  } catch (e) {
    u.alert(e.message, '', 'warning');
  }

  if (goToCart) {
    location.href = u.route('cart');
  } else {
    swal({
      title: '已加入購物車',
      buttons: {
        confirm: {
          text: '確認',
          value: true,
          className: 'btn btn-primary c-swal-confirm',
        },
      }
    });

    combinationStorage.beforeUnloadEnabled = true;
  }
}

function prepareItem() {
  const item = {};

  item.initial_variantId = combinationStorage?.initialItem?.id ?? null;
  item.terminal_variantId = combinationStorage?.terminalItem?.id ?? null;
  item.cables = [];

  Object.values(combinationStorage.cables).forEach((v) => {
    const cable = {};

    if (!v.allowNull) {
      cable.variantId = v.variant.id;
      cable.length = v.variant.length;
      cable.initial = {
        // 如果只有線材時 pin 為 null
        pin: combinationStorage.onlyCables ? null : v.initial.pin,
        categoryId: v.initial.category?.id
      };
      cable.terminal = {
        pin: v.terminal.pin,
        categoryId: v.terminal.category?.id
      };

      item.cables.push(cable);
    }
  });

  return item;
}

function updateCartButton(data) {
  const count = data.length;

  const $cartButtons = document.querySelectorAll('[data-role=cart-button]');

  for (const $cartButton of $cartButtons) {
    const $cartQuantity = $cartButton.querySelector('[data-role=cart-quantity]');

    $cartButton.classList.toggle('h-has-items', count > 0);
    $cartQuantity.textContent = count;
  }
}
</script>

<template>
  <div class="l-combination-product">
    <CombinationProductFlow></CombinationProductFlow>
    <component :is="flowComponent[combinationStorage.flowIndex]"></component>

    <section class="l-combination-product-flow__order-info">
      <div class="l-combination-product-flow__order-info-wrapper">
        <div class="container-fluid">
          <div class="row justify-content-between mt-4 mb-5">
            <div class="col-12 col-md-6 mt-md-3 mb-4 mb-md-0">
              <div class="d-flex gap-3 align-items-center" v-if="combinationStorage.flowIndex > 1">
                <span class="text-dark">組數:</span>
                <div class="l-combination-product-flow__number">
                  <div class="input-group input-number-group">
                    <div class="c-product-specification-btn j-product-btn__number-minus btn"
                      @click="handleMinusButton">
                      <i class="fa-regular fa-minus"></i>
                    </div>
                    <input class="j-product-input__number form-control text-center" type="number"
                      v-model="combinationStorage.quantity"
                      min="1" max="99" ref="numberInput">
                    <div class="c-product-specification-btn j-product-btn__number-plus btn"
                      @click="handlePlusButton">
                      <i class="fa-regular fa-plus"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-md-5 col-xl-3">
              <div class="d-flex justify-content-between align-items-center gap-2">
                <span class="text-dark">費用小計:</span>
                <div class="d-flex align-items-center gap-2">
                  <span class="l-combination-product-flow__nt fs-5">NT</span>
                  <span class="h3 mb-0">{{ $formatPrice(total, false, false, 1) }}</span>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-center gap-2"
                v-if="combinationStorage.flowIndex > 1">
                <span class="text-dark">費用合計:</span>
                <div class="d-flex align-items-center gap-2">
                  <span class="l-combination-product-flow__nt fs-5">NT</span>
                  <span class="h3 mb-0">{{ $formatPrice(total, false, false, 1) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col text-center">
              <div class="badge text-danger mb-2"
                :class="showTip ? 'd-block' : 'd-none'">{{ showTip }}
              </div>
              <button type="button" class="btn btn-primary"
                @click="toNextStep"
                v-if="combinationStorage.flowIndex !== 3">{{ chooseButtonText[combinationStorage.flowIndex] }}
              </button>

              <div class="l-combination-product__links d-flex flex-wrap gap-2 justify-content-center" v-else>
                <button type="button" class="btn btn-dark"
                  @click="addToCart(false)"
                >
                  加入購物車
                </button>
                <button type="button" class="btn btn-primary"
                  @click="addToCart(true)"
                >
                  前往結帳
                </button>
              </div>

              <a href="javascript:;" class="c-prev-btn mt-3 mx-auto"
                :class="combinationStorage.flowIndex === 0 ? 'd-none' : 'd-block'"
                @click="toPrevStep">
                <i class="fa-regular fa-chevron-left me-1"></i>
                {{ toPrevFlow[combinationStorage.flowIndex] }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
